<template>
  <div class="nutrients">
    <div class="header">Podstawowe wartości odżywcze</div>
    <div class="basic-nutrients">
      <div class="basic-nutrients__header">Nazwa</div>
      <div class="basic-nutrients__header basic-nutrients__header--assumption">Założenia diety</div>
      <div class="basic-nutrients__header basic-nutrients__header--norm">
        Suma / Docelowa wartość
      </div>
      <template v-for="nutrient in basicNutrients" :key="`name-${nutrient.key}`">
        <div class="basic-nutrients__cell basic-nutrients__name">
          {{ nutrient.name }}
          <span class="basic-nutrients__measurement">
            ({{ nutrientsMap[nutrient.key].measurement }}
            )
          </span>
        </div>
        <div class="basic-nutrients__cell basic-nutrients__assumption">
          <template v-if="nutrient.key === 'calories'">
            <BaseInputNumber
              class="assumption-input"
              test-id="DietDetailDayNutrients__Calories"
              :class="{ disabled: energyDemandSport || !canDietBeEdited }"
              :readonly="energyDemandSport"
              :min="1"
              :max="9999"
              :precision="0"
              :model-value="diet?.data.norms.calories ?? 0"
              @update:model-value="inputHandler(nutrient.key, $event)"
              @click="energyDemandSport ? disabledInputHandler() : ''"
            />
            <span>(kcal)</span>
          </template>
          <template v-else-if="nutrient.key === 'protein'">
            <BaseInputNumber
              class="assumption-input"
              test-id="DietDetailDayNutrients__Protein"
              :class="{ disabled: energyDemandSport || !canDietBeEdited }"
              :readonly="energyDemandSport"
              :min="0"
              :max="100"
              :precision="0"
              :model-value="diet?.data.calculation.standard.protein ?? 0"
              @update:model-value="inputHandler(nutrient.key, $event)"
              @click="energyDemandSport ? disabledInputHandler() : ''"
            />
            <span>%</span>
          </template>
          <template v-else-if="nutrient.key === 'fat'">
            <BaseInputNumber
              class="assumption-input"
              test-id="DietDetailDayNutrients__Fat"
              :class="{ disabled: energyDemandSport || !canDietBeEdited }"
              :readonly="energyDemandSport"
              :min="0"
              :max="100"
              :precision="0"
              :model-value="diet?.data.calculation.standard.fat ?? 0"
              @update:model-value="inputHandler(nutrient.key, $event)"
              @click="energyDemandSport ? disabledInputHandler() : ''"
            />
            <span>%</span>
          </template>
          <template v-else-if="nutrient.key === 'carbohydrates'">
            <BaseInputNumber
              class="assumption-input"
              test-id="DietDetailDayNutrients__Carbohydrates"
              :class="{ disabled: energyDemandSport || !canDietBeEdited }"
              :readonly="energyDemandSport"
              :min="0"
              :max="100"
              :precision="0"
              :model-value="diet?.data.calculation.standard.carbohydrates ?? 0"
              @update:model-value="inputHandler(nutrient.key, $event)"
              @click="energyDemandSport ? disabledInputHandler() : ''"
            />
            <span>%</span>
          </template>
          <template v-else-if="nutrient.key === 'fiber'">
            <BaseInputNumber
              class="assumption-input"
              test-id="DietDetailDayNutrients__Fiber"
              :class="{ disabled: energyDemandSport || !canDietBeEdited }"
              :readonly="energyDemandSport"
              :min="0"
              :max="1000"
              :precision="1"
              :model-value="diet?.data.norms.fiber?.inputed ?? 0"
              @update:model-value="inputHandler(nutrient.key, $event)"
              @click="energyDemandSport ? disabledInputHandler() : ''"
            />
            <span>(g)</span>
          </template>
        </div>
        <div class="basic-nutrients__cell basic-nutrients__norm">
          <div class="norm-box" :class="nutrient.normClass[dayIndex]">
            <DietDetailNutrientValue
              :difference="nutrient.differenceValue[dayIndex]"
              :measurement="nutrient.measurement"
              :norm="nutrient.norm"
              :norm-class="nutrient.normClass[dayIndex]"
              :percent="nutrient.valuePercent[dayIndex]"
              tooltip-placement="top-end"
              :value="nutrient.value[dayIndex]"
              variant="day"
            />
          </div>
        </div>
      </template>
    </div>
    <div class="header">Szczegółowe wartości odżywcze</div>
    <div v-if="globalStore.hasPerm('accounts.diet_microelements')" class="specific-nutrients">
      <div v-for="nutrient in choosenSpecificNutrients" :key="`specific-${nutrient.key}`">
        <div class="specific-nutrients__header">
          <div class="specific-nutrients__name" :title="nutrient.name">{{ nutrient.name }}</div>
          <DietDetailNutrientIncompleteData
            class="incomplete-data"
            :data="nutrient.incompleteData[dayIndex] ?? ''"
            variant="day"
          />
        </div>
        <div class="norm-box" :class="nutrient.normClass[dayIndex]">
          <DietDetailNutrientValue
            :difference="nutrient.differenceValue[dayIndex]"
            :measurement="nutrient.measurement"
            :norm="nutrient.norm"
            :norm-class="nutrient.normClass[dayIndex]"
            :percent="nutrient.valuePercent[dayIndex]"
            show-measurement
            :show-norm="nutrient.norm > 0"
            :value="nutrient.value[dayIndex]"
            variant="day"
          />
        </div>
      </div>
    </div>
    <div v-else class="specific-nutrients-disabled" @click="globalStore.showAppUpgradePlanModal" />
  </div>
</template>

<script lang="ts" setup>
  import { A, G } from '@mobily/ts-belt'
  import { storeToRefs } from 'pinia'
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'

  import BaseInputNumber from '@/components/BaseInputNumber.vue'
  import DietDetailNutrientIncompleteData from '@/components/diets/DietDetailNutrients/DietDetailNutrientIncompleteData.vue'
  import DietDetailNutrientValue from '@/components/diets/DietDetailNutrients/DietDetailNutrientValue.vue'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { nutrientsMap } from '@/utils/nutrients'

  import type { Nutrients } from '@/utils/nutrients'

  const dietStore = useDietsStore()
  const globalStore = useGlobalStore()
  const { canDietBeEdited, diet, basicNutrients, chosenNutrients, specificNutrients } =
    storeToRefs(dietStore)
  const route = useRoute()

  const dayIndex = computed(() => {
    if (G.isString(route.params.dayIndex)) {
      return parseInt(route.params.dayIndex)
    }
    return 0
  })
  const choosenSpecificNutrients = computed(() =>
    A.concat(chosenNutrients.value, specificNutrients.value),
  )
  const energyDemandSport = computed(() => diet.value?.data.calculation.type === 'sport')

  const disabledInputHandler = () => {
    globalStore.createMessage({
      title:
        'Zmień w ustawieniach diety sposób przeliczenia KBTW na Standardowy, aby odblokować edycję tego pola.',
    })
  }
  const inputHandler = (nutrientKey: Nutrients, value: number) => {
    if (nutrientKey === 'calories' || nutrientKey === 'fiber') {
      dietStore.updateNorm({ nutrient: nutrientKey, value })
    }
    if (['protein', 'fat', 'carbohydrates'].includes(nutrientKey)) {
      dietStore.updateCalculation({
        nutrient: nutrientKey as 'protein' | 'fat' | 'carbohydrates',
        type: 'standard',
        value,
      })
    }
  }
</script>

<style scoped lang="scss">
  .header {
    font-family: Montserrat-SemiBold;
    font-size: 16px;
    line-height: 40px;
    color: #fff;
    text-align: center;
    text-transform: uppercase;
    background: var(--color-main-100);
  }

  .basic-nutrients {
    display: grid;
    grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) minmax(190px, 1fr);
    padding-bottom: 24px;
  }

  .basic-nutrients__header {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 12px;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 1.21px;
    border-bottom: 2px solid #f8f8f8;
  }

  .basic-nutrients__header--assumption {
    padding: 0;
  }

  .basic-nutrients__header--norm {
    justify-content: flex-end;
    text-align: right;
  }

  .basic-nutrients__cell {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 12px;
    border-bottom: 2px solid #f8f8f8;
  }

  .basic-nutrients__name {
    justify-content: flex-start;
    font-family: Montserrat-Medium;
    font-size: 12px;
    line-height: 19px;
    letter-spacing: 1.21px;
  }

  .basic-nutrients__name .basic-nutrients__measurement {
    margin-left: 6px;
    color: var(--color-tertiary-text);
  }

  .basic-nutrients__assumption {
    justify-content: center;
    padding: 0;
  }

  .basic-nutrients__assumption .assumption-input {
    width: 72px;
    font-size: 14px;
    text-align: center;
  }

  .basic-nutrients__assumption .assumption-input.disabled {
    color: #000;
    border-bottom: none;
  }

  .basic-nutrients__assumption span {
    display: inline-block;
    width: 40px;
    margin-left: 6px;
    font-size: 14px;
  }

  .basic-nutrients__norm {
    justify-content: flex-end;
  }

  .specific-nutrients {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
    padding: 13px;
  }

  .specific-nutrients-disabled {
    height: 3000px;
    padding-bottom: 50px;
    background-image: url("@/assets/img/diets/specific-nutrients-blured.png");
    background-size: 100%;
  }

  .specific-nutrients__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    background: #f5f5f5;
  }

  .specific-nutrients__name {
    padding: 0 32px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    white-space: nowrap;
  }

  .incomplete-data {
    position: absolute;
    top: calc(50% - 8px);
    right: 6px;
    text-align: center;
  }

  .norm-box {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 9px;
    font-size: 14px;
  }

  .basic-nutrients__norm .norm-box {
    justify-content: flex-end;
    width: 160px;
    height: 100%;
  }

  .norm-box.red-up,
  .norm-box.red-down {
    background: var(--color-red-10);
  }

  .norm-box.yellow-up,
  .norm-box.yellow-down {
    background: var(--color-warning-10);
  }
</style>
